import { ReactNode } from "react";

type BannerTheme = "blue" | "amber" | "emerald" | "violet" | "slate";

// Per-theme gradient/border for the banner wrapper and the round icon badge.
const BANNER_THEMES: Record<BannerTheme, { wrap: string; badge: string }> = {
  blue: {
    wrap: "border-blue-200 dark:border-blue-900/60 bg-gradient-to-l from-blue-100 to-blue-50 dark:from-blue-950/60 dark:to-blue-950/25",
    badge: "bg-blue-500/15 text-blue-600",
  },
  amber: {
    wrap: "border-amber-200 dark:border-amber-900/60 bg-gradient-to-l from-amber-100 to-amber-50 dark:from-amber-950/60 dark:to-amber-950/25",
    badge: "bg-amber-500/15 text-amber-600",
  },
  emerald: {
    wrap: "border-emerald-200 dark:border-emerald-900/60 bg-gradient-to-l from-emerald-100 to-emerald-50 dark:from-emerald-950/60 dark:to-emerald-950/25",
    badge: "bg-emerald-500/15 text-emerald-600",
  },
  violet: {
    wrap: "border-violet-200 dark:border-violet-900/60 bg-gradient-to-l from-violet-100 to-violet-50 dark:from-violet-950/60 dark:to-violet-950/25",
    badge: "bg-violet-500/15 text-violet-600",
  },
  slate: {
    wrap: "border-slate-200 dark:border-slate-700/60 bg-gradient-to-l from-slate-100 to-slate-50 dark:from-slate-800/60 dark:to-slate-800/25",
    badge: "bg-slate-500/15 text-slate-500",
  },
};

function WalletBanner({
  theme = "blue",
  Icon,
  action,
  children,
  className = "",
}: {
  theme?: BannerTheme;
  Icon?: React.ComponentType<any>;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const t = BANNER_THEMES[theme] ?? BANNER_THEMES.blue;
  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${t.wrap} ${className}`}>
      {/* Mobile: icon + action share the top row (split apart), content wraps to
          a full-width row below so it has room to breathe.
          Desktop: icon + content sit together, action pushed to the far end. */}
      <div className="flex flex-wrap items-start gap-4 md:flex-nowrap md:items-center">
        {/* icon — always first */}
        {Icon && (
          <div className={`order-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${t.badge}`}>
            <Icon style={{ fontSize: 24 }} />
          </div>
        )}

        {/* action — pushed to the row end (top-end on mobile, far end on desktop) */}
        {action && (
          <div className="order-2 ms-auto shrink-0 md:order-3">
            {action}
          </div>
        )}

        {/* content — children render as direct flex items so callers can place
            multiple side-by-side blocks (e.g. principal + remaining debt).
            Full width on mobile (drops to its own row), auto width on desktop. */}
        <div className="order-3 flex w-full flex-wrap items-center gap-4 md:order-2 md:w-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

export default WalletBanner;
